import { useEffect } from 'react';
import { useDragDropManager, useDragLayer } from 'react-dnd';

import { MouseEventType } from '@this+that/utils';
import type { DragDropManager } from 'dnd-core';

// Mirrors HTML5BackendImpl's MOUSE_MOVE_TIMEOUT: some browsers fire one
// mousemove right after dragstart before the preview is drawn.
const STALE_DRAG_RECOVERY_ARM_DELAY_MS = 1000;

function endDragIfStillDragging(manager: DragDropManager): void {
  if (manager.getMonitor().isDragging()) {
    manager.getActions().endDrag();
  }
}

function addRecoveryListeners(handler: () => void): void {
  window.addEventListener(MouseEventType.MOUSEMOVE, handler, {
    capture: true,
  });
  window.addEventListener(MouseEventType.MOUSEDOWN, handler, {
    capture: true,
  });
}

function removeRecoveryListeners(handler: () => void): void {
  window.removeEventListener(MouseEventType.MOUSEMOVE, handler, {
    capture: true,
  });
  window.removeEventListener(MouseEventType.MOUSEDOWN, handler, {
    capture: true,
  });
}

export function useStaleDragRecovery(enabled: boolean): void {
  const manager = useDragDropManager();
  const isDragging = useDragLayer((monitor) => monitor.isDragging());

  useEffect(() => {
    if (!enabled || !isDragging) {
      return;
    }

    const endStaleDrag = () => endDragIfStillDragging(manager);
    const timer = window.setTimeout(
      () => addRecoveryListeners(endStaleDrag),
      STALE_DRAG_RECOVERY_ARM_DELAY_MS,
    );

    return () => {
      window.clearTimeout(timer);
      removeRecoveryListeners(endStaleDrag);
    };
  }, [enabled, isDragging, manager]);
}
